import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, GripVertical, Eye } from 'lucide-react';

/* ── Column width persistence (localStorage per tableId per browser) ──── */

function loadColumnWidths(tableId) {
  if (!tableId) return {};
  try {
    const raw = localStorage.getItem(`datalist_colwidths_${tableId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveColumnWidths(tableId, widths) {
  if (!tableId) return;
  try { localStorage.setItem(`datalist_colwidths_${tableId}`, JSON.stringify(widths)); } catch {}
}

/* ── Hidden columns persistence (per tableId per browser) ──────────────── */
// Stores the explicit user choice as an array of column keys. If no value is
// stored yet, falls back to the columns' `defaultHidden` flag — so a page can
// ship a column hidden-by-default that the user can reveal once and keep.

function loadHiddenCols(tableId, columns) {
  if (tableId) {
    try {
      const raw = localStorage.getItem(`datalist_hidden_${tableId}`);
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
  }
  return new Set((columns || []).filter(c => c.defaultHidden).map(c => c.key));
}

function saveHiddenCols(tableId, set) {
  if (!tableId) return;
  try { localStorage.setItem(`datalist_hidden_${tableId}`, JSON.stringify([...set])); } catch {}
}

/**
 * DataList - Reusable list component with sorting, pagination, row click, column reorder
 *
 * Props:
 *   data: Array of items
 *   columns: [{ key: string, label: string, sortable?: boolean, render?: (item) => ReactNode, width?: string }]
 *   onRowClick: (item) => void - called when row is clicked
 *   rowKeyField: string - field to use as key (default: 'id')
 *   selectable: boolean - show checkboxes
 *   selectedIds: Set - selected item IDs
 *   onSelectionChange: (newSet) => void
 *   defaultSort: { key: string, direction: 'asc' | 'desc' }
 *   defaultPageSize: number (default: 25)
 *   hoverPreview: (item) => ReactNode - optional hover preview content
 *   emptyMessage: string
 *   tableId: string - if provided, enables column reorder with persistence
 *   columnOrder: string[] - persisted column key order (from usePreferences)
 *   onColumnOrderChange: (string[]) => void - called when columns are reordered
 *   highlightedRowKey: any - if provided, the row whose rowKeyField equals this value
 *     gets a "selected/active" highlight (independent of selectable checkboxes).
 *     Useful for master/detail split views (clic ligne -> aperçu).
 *   renderRowDetail: (item) => ReactNode | null - optionnelle. Rend une sous-ligne pleine
 *     largeur JUSTE sous la ligne de l'élément quand elle renvoie du contenu (ligne dépliée).
 *     L'état « déplié » appartient à la page ; un clic dans la sous-ligne n'est pas un clic de ligne.
 */
export default function DataList({
  data = [],
  columns = [],
  onRowClick,
  onRowDoubleClick,
  onVisibleOrderChange,
  rowKeyField = 'id',
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  totalCount,          // taille du jeu complet (pagination serveur) : active le bandeau « sélectionner les N résultats »
  onSelectAllResults,  // () => Promise : la page fournit les identifiants du jeu complet
  defaultSort = null,
  // Optionnelle. Fournie, le tri est CONTRÔLÉ par le parent : { key, direction }.
  // À utiliser dès que le tri déclenche un rechargement (voir plus bas).
  sort: controlledSort,
  // Optionnelle. Fournie, elle délègue la pagination au parent :
  // { page, setPage, total, pageSize, onPageSizeChange }. Voir plus bas.
  serverPagination,
  // Optionnelle. Fournie, elle délègue le tri au parent : ({key, direction}) à
  // chaque clic d'en-tête, pour les listes paginées côté SERVEUR où trier
  // localement ne réordonnerait qu'une page. Absente, comportement inchangé.
  onSortChange,
  // Optionnelle. Vraie, les données arrivent DÉJÀ dans l'ordre voulu (tri serveur sur plusieurs
  // niveaux) : aucun tri local. Retrier sur la seule colonne affichée défaisait les niveaux
  // suivants, et sur une colonne dont la valeur n'est pas dans la ligne, mélangeait la page.
  triServeur = false,
  defaultPageSize = 25,
  hoverPreview,
  emptyMessage = 'Aucune donnee',
  tableId,
  columnOrder,
  onColumnOrderChange,
  groupBy,
  groupLabel,
  defaultCollapsed,
  highlightedRowKey,
  // Si fourni, transforme la ligne en lien navigable : clic-droit → "Ouvrir dans un nouvel onglet"
  // (menu natif browser via <a href>), Ctrl/Cmd+clic et clic-milieu → nouvel onglet automatique.
  // Le clic-gauche standard reste géré par onRowClick (preventDefault sur le <a>).
  getRowHref,
  renderRowDetail,
  // Optionnelle (09/10/2026). Vraie, toutes les lignes s'affichent et le pied de pagination
  // disparaît : pour une table courte posée DANS un bloc (un jour de « Mes interventions »),
  // où un pied répété sous chaque bloc encombrerait la page.
  sansPagination = false,
}) {
  // Column reorder
  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;
    const byKey = Object.fromEntries(columns.map(c => [c.key, c]));
    const ordered = columnOrder.map(k => byKey[k]).filter(Boolean);
    // Append any new columns not in the saved order
    const savedSet = new Set(columnOrder);
    columns.forEach(c => { if (!savedSet.has(c.key)) ordered.push(c); });
    return ordered;
  }, [columns, columnOrder]);

  const dragCol = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const handleColDragStart = useCallback((e, key) => {
    dragCol.current = key;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  }, []);
  const handleColDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);
  const handleColDragEnter = useCallback((key) => { if (dragCol.current && dragCol.current !== key) setDragOverCol(key); }, []);
  const handleColDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    if (!dragCol.current || dragCol.current === targetKey) { dragCol.current = null; setDragOverCol(null); return; }
    const keys = orderedColumns.map(c => c.key);
    const fromIdx = keys.indexOf(dragCol.current);
    const toIdx = keys.indexOf(targetKey);
    if (fromIdx !== -1 && toIdx !== -1) {
      const newKeys = [...keys];
      const [moved] = newKeys.splice(fromIdx, 1);
      newKeys.splice(toIdx, 0, moved);
      if (onColumnOrderChange) onColumnOrderChange(newKeys);
    }
    dragCol.current = null;
    setDragOverCol(null);
  }, [orderedColumns, onColumnOrderChange]);
  const handleColDragEnd = useCallback(() => { dragCol.current = null; setDragOverCol(null); }, []);

  const canReorderColumns = Boolean(onColumnOrderChange);

  // Hidden columns (per-user, per-browser via tableId). Columns can ship as
  // hidden-by-default via `column.defaultHidden`; the user reveals them once
  // and the choice persists.
  const [hiddenCols, setHiddenCols] = useState(() => loadHiddenCols(tableId, columns));
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  useEffect(() => { saveHiddenCols(tableId, hiddenCols); }, [tableId, hiddenCols]);

  // Close col picker on outside click
  useEffect(() => {
    if (!showColPicker) return;
    const onClickOutside = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) {
        setShowColPicker(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showColPicker]);

  const toggleColHidden = useCallback((key) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const visibleColumns = useMemo(
    () => orderedColumns.filter(c => !hiddenCols.has(c.key)),
    [orderedColumns, hiddenCols],
  );

  // Tri CONTRÔLABLE. Quand le parent fournit `sort`, c'est lui qui détient la
  // vérité — indispensable dès que le tri déclenche un rechargement : la liste
  // remplace alors <DataList/> par « Chargement... », donc le DÉMONTE, et un
  // état interne repartirait de `defaultSort` au remontage. Le serveur ayant
  // trié en ascendant, le composant remonté re-triait en descendant : le clic
  // paraissait sans effet (constaté en prod le 21/09/2026).
  const [internalSort, setInternalSort] = useState(defaultSort || { key: columns[0]?.key || '', direction: 'asc' });
  const sort = controlledSort || internalSort;
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(defaultPageSize);

  // Pagination déléguée au SERVEUR (prop optionnelle). Fournie, la page reçue
  // est déjà la bonne tranche : la redécouper masquerait des lignes que le
  // serveur a délibérément envoyées, et compter les pages sur ce lot ferait
  // afficher « Page 1 / 2 » sur une file de plusieurs centaines. Absente, tout
  // reste local, comme pour les autres tables.
  const srvPag = serverPagination || null;
  const page = srvPag ? srvPag.page : localPage;
  const setPage = srvPag ? srvPag.setPage : setLocalPage;
  const pageSize = srvPag ? srvPag.pageSize : localPageSize;
  const setPageSize = srvPag
    ? (n) => srvPag.onPageSizeChange && srvPag.onPageSizeChange(n)
    : setLocalPageSize;
  const [hoveredId, setHoveredId] = useState(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const mouseMoveHandler = useCallback((e) => {
    mousePosRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  // Sync mousePos state only when hoveredId changes (not on every pixel move)
  useEffect(() => {
    if (hoveredId) setMousePos({ ...mousePosRef.current });
  }, [hoveredId]);

  // ── Column resize ──
  const [colWidths, setColWidths] = useState(() => loadColumnWidths(tableId));
  const resizeRef = useRef(null); // { key, startX, startW }

  useEffect(() => { saveColumnWidths(tableId, colWidths); }, [tableId, colWidths]);

  const handleResizeStart = useCallback((e, colKey, thEl) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = thEl.getBoundingClientRect().width;
    resizeRef.current = { key: colKey, startX, startW };

    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const diff = ev.clientX - resizeRef.current.startX;
      const newW = Math.max(50, resizeRef.current.startW + diff);
      setColWidths(prev => ({ ...prev, [resizeRef.current.key]: newW }));
    };
    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const getColWidth = useCallback((col) => {
    if (colWidths[col.key]) return colWidths[col.key];
    return col.width || undefined;
  }, [colWidths]);

  // La SEULE lecture de `columns` dont dépend le tri : la fonction sortValue de
  // la colonne triée. La mettre en dépendance (et non `columns`, recréé à chaque
  // rendu par la plupart des pages) relance le tri quand un libellé chargé après
  // coup change la fonction, sans re-trier à chaque rendu les colonnes sans
  // sortValue (500 lignes texte = ~28 ms par tri, mesuré le 23/09/2026).
  const sortCol = columns.find(c => c.key === sort.key);
  const sortValueFn = sortCol && typeof sortCol.sortValue === 'function' ? sortCol.sortValue : null;
  // Sort data (empty/null values always sort last regardless of direction)
  const sortedData = useMemo(() => {
    if (triServeur || !sort.key) return data;
    // `sortValue` : trois pages le passaient déjà (Ordinateurs, Agents Meteris,
    // Inventaire) pour trier un objet, une clé absente ou un numéro stocké en
    // chaîne — la prop n'existait pas, le tri était mort. Sans sortValue,
    // comportement inchangé.
    const getVal = (row) => (sortValueFn ? sortValueFn(row) : row[sort.key]);
    return [...data].sort((a, b) => {
      const aVal = getVal(a);
      const bVal = getVal(b);

      const aEmpty = aVal == null || aVal === '';
      const bEmpty = bVal == null || bVal === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal, 'fr', { sensitivity: 'base' });
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal), 'fr');
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sort, sortValueFn, triServeur]);

  // Ordre VISIBLE aplati (tri + groupement GLOBAL par groupBy) exposé au parent, pour
  // permettre une navigation Précédent/Suivant fidèle à la liste depuis une fiche détail.
  // NB : le groupement d'affichage est par page ; on expose ici l'ordre groupé GLOBALEMENT
  // (sociétés regroupées dans l'ordre d'apparition, triées à l'intérieur) — stable et
  // intuitif au clavier (pas de saut aux frontières de page).
  const flatVisibleOrder = useMemo(() => {
    if (!groupBy) return sortedData;
    const gk = Array.isArray(groupBy) ? groupBy[0] : groupBy;
    if (!gk) return sortedData;
    const groups = new Map();
    for (const item of sortedData) {
      const key = item[gk] ?? '—';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    return [].concat(...groups.values());
  }, [sortedData, groupBy]);

  useEffect(() => {
    if (onVisibleOrderChange) onVisibleOrderChange(flatVisibleOrder);
  }, [flatVisibleOrder, onVisibleOrderChange]);

  // Paginate. En mode serveur, le nombre de pages se compte sur le TOTAL annoncé
  // par le serveur : le sortedData local n'est qu'une tranche.
  const totalPages = srvPag
    ? Math.ceil((srvPag.total || 0) / pageSize)
    : Math.ceil(sortedData.length / pageSize);
  // Fix defensif : clamp page pour eviter slice() vide quand page > totalPages
  // (peut arriver si data.length passe de N>pageSize a 1, et page persiste a la valeur > 1).
  // Symptome : emptyMessage affiche alors que items presents.
  const safePage = Math.min(Math.max(1, page), Math.max(1, totalPages));
  const paginatedData = useMemo(() => {
    // Mode serveur : la tranche est déjà faite en amont, on affiche tout.
    if (srvPag || sansPagination) return sortedData;
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, safePage, pageSize, srvPag, sansPagination]);

  // Reset page state quand desynchronise (effet de bord propre, pas useMemo)
  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
    // `setPage` est ici une variable dérivée (locale ou celle du parent en mode
    // serveur) : contrairement à un setter useState, sa stabilité n'est pas
    // garantie à l'analyse. Pas de boucle possible — l'appel n'a lieu que si la
    // page est désynchronisée, et il la resynchronise.
  }, [page, safePage, setPage]);

  const handleSort = (key) => {
    const col = columns.find(c => c.key === key);
    if (!col?.sortable) return;

    // `sort` vient du rendu courant : on calcule l'état suivant ICI plutôt que
    // dans l'updater de setSort, qui doit rester pur (React peut le rejouer).
    const next = {
      key,
      direction: sort.key === key && sort.direction === 'asc' ? 'desc' : 'asc',
    };
    // En mode contrôlé, l'état interne n'est pas la vérité : le parent renverra
    // le nouveau tri par la prop. L'écrire quand même créerait deux sources.
    if (!controlledSort) setInternalSort(next);
    // Tables paginées côté SERVEUR : trier localement ne réordonnerait que la
    // page reçue, donc un échantillon. Le parent est prévenu pour recharger le
    // jeu complet dans le bon ordre. Le tri local reste appliqué en attendant
    // sa réponse, pour ne pas laisser la liste dans un ordre arbitraire — et
    // pour ne rien changer aux tables qui ne passent pas cette prop.
    if (onSortChange) onSortChange(next);
  };

  const toggleSelectAll = () => {
    if (!onSelectionChange) return;
    const currentPageIds = paginatedData.map(item => item[rowKeyField]);
    const allSelected = currentPageIds.every(id => selectedIds.has(id));
    
    const newSet = new Set(selectedIds);
    if (allSelected) {
      currentPageIds.forEach(id => newSet.delete(id));
    } else {
      currentPageIds.forEach(id => newSet.add(id));
    }
    onSelectionChange(newSet);
  };

  // Track last clicked index for shift-click range selection
  const lastClickedIdxRef = useRef(null);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;

    const currentIdx = paginatedData.findIndex(item => item[rowKeyField] === id);

    // Shift-click → select range from last clicked to current
    if (e.shiftKey && lastClickedIdxRef.current !== null && currentIdx !== -1) {
      const start = Math.min(lastClickedIdxRef.current, currentIdx);
      const end = Math.max(lastClickedIdxRef.current, currentIdx);
      const newSet = new Set(selectedIds);
      // Determine target state: if current is selected, deselect range; else select
      const shouldSelect = !selectedIds.has(id);
      for (let i = start; i <= end; i++) {
        const rowId = paginatedData[i][rowKeyField];
        if (shouldSelect) newSet.add(rowId);
        else newSet.delete(rowId);
      }
      onSelectionChange(newSet);
      lastClickedIdxRef.current = currentIdx;
      return;
    }

    // Normal click → toggle single
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    onSelectionChange(newSet);
    lastClickedIdxRef.current = currentIdx;
  };

  const currentPageIds = paginatedData.map(item => item[rowKeyField]);
  const allSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id));
  const someSelected = currentPageIds.some(id => selectedIds.has(id));

  // ── Group by support ──
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    if (!defaultCollapsed) return {};
    const init = {};
    if (Array.isArray(defaultCollapsed)) defaultCollapsed.forEach(g => { init[String(g ?? '—')] = true; });
    return init;
  });
  const toggleGroup = useCallback((groupKey) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  const groupedPageData = useMemo(() => {
    if (!groupBy) return null;
    // Support multi-groupBy: groupBy can be a string or array of strings
    const groupKeys = Array.isArray(groupBy) ? groupBy : [groupBy];
    if (groupKeys.length === 0) return null;

    const buildGroups = (items, keyIdx) => {
      const gk = groupKeys[keyIdx];
      if (!gk) return null;
      const groups = [];
      const map = {};
      for (const item of items) {
        const raw = item[gk];
        const key = String(raw ?? '—');
        const label = groupLabel ? groupLabel(raw, item, gk) : key;
        if (!map[key]) {
          map[key] = { key: `${keyIdx}:${key}`, label, items: [], depth: keyIdx };
          groups.push(map[key]);
        }
        map[key].items.push(item);
      }
      // Recurse for next level
      if (keyIdx + 1 < groupKeys.length) {
        for (const g of groups) {
          g.subgroups = buildGroups(g.items, keyIdx + 1);
        }
      }
      return groups;
    };
    return buildGroups(paginatedData, 0);
  }, [paginatedData, groupBy, groupLabel]);

  // Helper : wrap cell content dans un <a> quand getRowHref est fourni.
  // Permet : clic-droit "Ouvrir dans un nouvel onglet" (menu natif), Ctrl/Cmd+clic
  // et middle-click → nouvel onglet automatique. Le clic gauche standard
  // déclenche onRowClick via preventDefault.
  const wrapCell = (item, content) => {
    const href = getRowHref?.(item);
    if (!href) return content;
    return (
      <a href={href}
         style={{ color: 'inherit', textDecoration: 'none', display: 'block', width: '100%', height: '100%' }}
         onClick={(e) => {
           // Modifiers (Ctrl/Cmd/Shift) ou middle/right click → laisser le browser gérer
           if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
           e.preventDefault();
           onRowClick && onRowClick(item);
         }}>
        {content}
      </a>
    );
  };

  // Sous-ligne dépliée (renderRowDetail) : pleine largeur, case de sélection comprise.
  const renderDetailRow = (item) => {
    const detail = renderRowDetail ? renderRowDetail(item) : null;
    if (detail === null || detail === undefined || detail === false) return null;
    return (
      <tr key={`${item[rowKeyField]}__detail`} data-row-detail="true"
        style={{ background: '#f8fafc', borderBottom: '1px solid #e8f0fe' }}>
        <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} style={{ ...tdStyle, padding: '8px 12px 12px 36px' }}>
          {detail}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        {selectable && onSelectionChange && onSelectAllResults && totalCount > paginatedData.length && paginatedData.length > 0
          && paginatedData.every(item => selectedIds.has(item[rowKeyField])) && (
          // « Tout sélectionner » ne couvrait que la page affichée sans le dire (proposition UI validée le 07/09/2026).
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', fontSize: 12, background: '#e8f0fe', borderBottom: '1px solid #c0cfe0', color: '#1a3a5c' }}>
            {selectedIds.size >= totalCount
              ? <span>Les <b>{totalCount}</b> résultats sont sélectionnés.</span>
              : <span>Les <b>{paginatedData.length}</b> lignes de cette page sont sélectionnées.</span>}
            {selectedIds.size >= totalCount
              ? <button type="button" onClick={() => onSelectionChange(new Set())} style={{ background: 'none', border: 'none', color: '#2563a8', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Effacer la sélection</button>
              : <button type="button" onClick={() => onSelectAllResults()} style={{ background: 'none', border: 'none', color: '#2563a8', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Sélectionner les {totalCount} résultats</button>}
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {selectable && (
                <th style={{ ...thStyle, width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => el && (el.indeterminate = someSelected && !allSelected)}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              {visibleColumns.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={{
                    ...thStyle,
                    width: getColWidth(col),
                    minWidth: getColWidth(col) ? undefined : 50,
                    cursor: col.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    position: 'relative',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {col.label}
                    {col.sortable && sort.key === col.key && (
                      sort.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    )}
                  </div>
                  {/* Resize handle */}
                  {tableId && (
                    <div
                      onMouseDown={(e) => handleResizeStart(e, col.key, e.target.closest('th'))}
                      style={{
                        position: 'absolute', right: -1, top: 4, bottom: 4, width: 4,
                        cursor: 'col-resize', zIndex: 2, borderRadius: 2,
                        background: 'var(--border-light)',
                        transition: 'background 0.15s, width 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary-light)'; e.currentTarget.style.width = '5px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--border-light)'; e.currentTarget.style.width = '4px'; }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} style={{ ...tdStyle, textAlign: 'center', color: '#6b7d8f', padding: 24 }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : groupedPageData ? (
              (() => {
                const GROUP_COLORS = ['#f0f4fb', '#f8fafc', '#fefce8'];
                const renderRow = (item) => {
                  const id = item[rowKeyField];
                  const isSelected = selectedIds.has(id);
                  const isHovered = hoveredId === id;
                  const isHighlighted = highlightedRowKey !== undefined && highlightedRowKey !== null && id === highlightedRowKey;
                  return (
                    <React.Fragment key={id}>
                    <tr
                      onClick={(getRowHref && getRowHref(item)) ? undefined : () => onRowClick && onRowClick(item)}
                      onDoubleClick={() => onRowDoubleClick?.(item)}
                      onMouseEnter={() => setHoveredId(id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onMouseMove={mouseMoveHandler}
                      style={{
                        borderBottom: '1px solid #e8f0fe',
                        background: isHighlighted ? '#dbeafe' : isSelected ? '#e8f0fe' : isHovered ? '#f8fafc' : 'transparent',
                        boxShadow: isHighlighted ? 'inset 3px 0 0 #2563eb' : 'none',
                        cursor: (onRowClick || getRowHref) ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                      }}
                    >
                      {selectable && (
                        <td style={tdStyle} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onClick={(e) => toggleSelect(id, e)} onChange={() => {}} title="Shift+clic pour sélectionner une plage" style={{ cursor: 'pointer' }} />
                        </td>
                      )}
                      {visibleColumns.map(col => (
                        <td key={col.key} style={{ ...tdStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: colWidths[col.key] ? 'nowrap' : undefined }}>
                          {wrapCell(item, col.render ? col.render(item) : item[col.key] ?? '-')}
                        </td>
                      ))}
                      {hoverPreview && isHovered && hoverPreview(item) && ReactDOM.createPortal(
                        <div style={getPreviewStyle(mousePos.x, mousePos.y)}>{hoverPreview(item)}</div>,
                        document.body
                      )}
                    </tr>
                    {renderDetailRow(item)}
                    </React.Fragment>
                  );
                };
                const renderGroups = (groups) => groups.map(group => {
                  const isCollapsed = collapsedGroups[group.key];
                  const depth = group.depth || 0;
                  return (
                    <React.Fragment key={`grp-${group.key}`}>
                      <tr
                        onClick={() => toggleGroup(group.key)}
                        style={{
                          background: GROUP_COLORS[depth % GROUP_COLORS.length],
                          cursor: 'pointer', userSelect: 'none',
                          borderBottom: depth === 0 ? '2px solid var(--border)' : '1px solid var(--border)',
                        }}
                      >
                        <td
                          colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                          style={{
                            padding: `8px ${14 + depth * 16}px`,
                            fontWeight: depth === 0 ? 700 : 600,
                            fontSize: 12 - depth, color: 'var(--primary)',
                            letterSpacing: 0.3,
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            {group.label}
                            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>({group.items.length})</span>
                          </span>
                        </td>
                      </tr>
                      {!isCollapsed && (
                        group.subgroups
                          ? renderGroups(group.subgroups)
                          : group.items.map(renderRow)
                      )}
                    </React.Fragment>
                  );
                });
                return renderGroups(groupedPageData);
              })()
            ) : (
              paginatedData.map(item => {
                const id = item[rowKeyField];
                const isSelected = selectedIds.has(id);
                const isHovered = hoveredId === id;
                const isHighlighted = highlightedRowKey !== undefined && highlightedRowKey !== null && id === highlightedRowKey;

                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={(getRowHref && getRowHref(item)) ? undefined : () => onRowClick && onRowClick(item)}
                      onDoubleClick={() => onRowDoubleClick?.(item)}
                      onMouseEnter={() => setHoveredId(id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onMouseMove={mouseMoveHandler}
                      style={{
                        borderBottom: '1px solid #e8f0fe',
                        background: isHighlighted ? '#dbeafe' : isSelected ? '#e8f0fe' : isHovered ? '#f8fafc' : 'transparent',
                        boxShadow: isHighlighted ? 'inset 3px 0 0 #2563eb' : 'none',
                        cursor: (onRowClick || getRowHref) ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                      }}
                    >
                      {selectable && (
                        <td style={tdStyle} onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => toggleSelect(id, e)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                      )}
                      {visibleColumns.map(col => (
                        <td key={col.key} style={{ ...tdStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: colWidths[col.key] ? 'nowrap' : undefined }}>
                          {wrapCell(item, col.render ? col.render(item) : item[col.key] ?? '-')}
                        </td>
                      ))}
                      {hoverPreview && isHovered && hoverPreview(item) && ReactDOM.createPortal(
                        <div style={getPreviewStyle(mousePos.x, mousePos.y)}>{hoverPreview(item)}</div>,
                        document.body
                      )}
                    </tr>
                    {renderDetailRow(item)}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.length > 0 && !sansPagination && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderTop: '1px solid #e8f0fe',
          marginTop: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7d8f' }}>Afficher</span>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={selectStyle}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: '#6b7d8f' }}>
              {/* Mode serveur : `data` n'est qu'une page. Annoncer sa taille
                  ferait lire « sur 50 elements » sur une file de 300. */}
              sur {srvPag ? (srvPag.total || 0) : data.length} element{(srvPag ? (srvPag.total || 0) : data.length) > 1 ? 's' : ''}
            </span>

            {/* Column visibility picker */}
            <div ref={colPickerRef} style={{ position: 'relative', marginLeft: 8 }}>
              <button
                onClick={() => setShowColPicker(v => !v)}
                title="Choisir les colonnes affichées"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 8px', border: '1px solid #d0d7de',
                  borderRadius: 4, background: '#fff',
                  color: '#6b7d8f', fontSize: 11, cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                }}
              >
                <Eye size={12} /> Colonnes
                {hiddenCols.size > 0 && (
                  <span style={{ background: '#e8f0fe', color: 'var(--primary)', padding: '0 4px', borderRadius: 8, fontSize: 10, fontWeight: 600 }}>
                    -{hiddenCols.size}
                  </span>
                )}
              </button>
              {showColPicker && (
                <div style={{
                  position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                  background: '#fff', border: '1px solid #d0d7de', borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '6px 0',
                  minWidth: 200, zIndex: 100, maxHeight: 320, overflowY: 'auto',
                }}>
                  {orderedColumns.map(col => {
                    if (!col.label) return null; // skip unlabeled (actions)
                    const isVisible = !hiddenCols.has(col.key);
                    return (
                      <label
                        key={col.key}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 12px', cursor: 'pointer', fontSize: 12,
                          color: '#1a3a5c',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColHidden(col.key)}
                          style={{ cursor: 'pointer' }}
                        />
                        {col.label}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Ces boutons n'ont que des icones : sans `title`, ils n'ont aucun
                nom accessible — ni pour un lecteur d'ecran, ni pour un test. */}
            <button onClick={() => setPage(1)} disabled={page === 1} style={paginationBtn} title="Première page">
              <ChevronsLeft size={14} />
            </button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={paginationBtn} title="Page précédente">
              <ChevronLeft size={14} />
            </button>

            <span style={{ padding: '0 12px', fontSize: 12, color: '#1a3a5c' }}>
              Page {page} / {totalPages || 1}
            </span>

            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={paginationBtn} title="Page suivante">
              <ChevronRight size={14} />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} style={paginationBtn} title="Dernière page">
              <ChevronsRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  background: 'var(--bg)',
  color: 'var(--primary)',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  borderBottom: '2px solid var(--border)',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontSize: 13,
};

const selectStyle = {
  padding: '4px 8px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  background: 'var(--surface)',
  cursor: 'pointer',
  transition: 'border-color var(--duration-fast)',
};

const paginationBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
  cursor: 'pointer',
  color: 'var(--primary)',
  transition: 'all var(--duration-fast)',
};

const getPreviewStyle = (mouseX, mouseY) => {
  const popupW = 320;
  const offsetX = 16;
  const left = mouseX + offsetX + popupW > window.innerWidth
    ? mouseX - popupW - offsetX
    : mouseX + offsetX;
  const top = Math.max(8, Math.min(mouseY - 10, window.innerHeight - 320));
  return {
    position: 'fixed',
    left,
    top,
    width: popupW,
    background: 'var(--primary)',
    color: '#fff',
    padding: '14px 16px',
    borderRadius: 10,
    boxShadow: 'var(--shadow-xl)',
    border: '1px solid rgba(255,255,255,0.08)',
    zIndex: 9999,
    fontSize: 12,
    lineHeight: 1.6,
    pointerEvents: 'none',
    animation: 'tooltip-in 150ms cubic-bezier(0.16, 1, 0.3, 1)',
  };
};
